import { useState, PropsWithChildren, ReactNode } from 'react';

import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';

import { safeLocalStorageRead, safeLocalStorageWrite } from '@/lib/storage/localStorage';
import { cn } from '@/lib/utils';

type CollapsibleSectionProps = {
  title: ReactNode;
  open?: boolean;
  showBorder?: boolean;
  keepOpen?: boolean;
  rightIcon?: ReactNode;
  onToggle?: (isOpen: boolean) => void;
  className?: string;
  storageKey?: string;
};

export const CollapsibleSection = ({
  children,
  title,
  open = true,
  showBorder = true,
  keepOpen = false,
  rightIcon,
  onToggle,
  className,
  storageKey,
}: PropsWithChildren<CollapsibleSectionProps>) => {
  const [isOpen, setIsOpen] = useState(() => {
    if (storageKey) {
      const stored = safeLocalStorageRead(storageKey);
      return stored !== null ? stored === 'true' : open;
    }
    return open;
  });

  const toggleOpen = () => {
    setIsOpen((prev) => {
      const newState = !prev;
      if (storageKey) {
        safeLocalStorageWrite(storageKey, String(newState));
      }
      onToggle?.(newState);
      return newState;
    });
  };

  const Chevron = () => {
    if (keepOpen) {
      return null;
    }
    if (isOpen) {
      return <ChevronDownIcon size={16} className="text-foreground-muted" />;
    } else {
      return <ChevronRightIcon size={16} className="text-foreground-muted" />;
    }
  };

  return (
    <div className={cn('group flex flex-col gap-px', !isOpen && 'mb-1', className)}>
      <div
        role={!keepOpen ? 'button' : undefined}
        tabIndex={!keepOpen ? 0 : undefined}
        aria-expanded={!keepOpen ? isOpen : undefined}
        onClick={!keepOpen ? toggleOpen : undefined}
        onKeyDown={
          !keepOpen
            ? (e) => {
                if (e.target !== e.currentTarget) {
                  return;
                }
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleOpen();
                }
              }
            : undefined
        }
        className={cn(
          'flex h-9 items-center gap-1.5 rounded px-1.5 transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          !keepOpen && 'cursor-pointer hover:bg-highlight-background',
          showBorder && 'border-b border-border'
        )}
      >
        <span className="flex flex-1 items-center gap-1.5 text-left text-xs leading-5 font-medium text-foreground-muted">
          <Chevron />
          <span>{title}</span>
        </span>
        {rightIcon && (
          <div className="flex h-6 w-6 shrink-0 items-center justify-center">{rightIcon}</div>
        )}
      </div>
      {isOpen && <div className="flex flex-col gap-px">{children}</div>}
    </div>
  );
};
